import type { FunctionComponent, Ref } from "react";
import { useCallback } from "react";

import { theme } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import { TargetingPaidType } from "scmp-app/components/advertisement/ad-slots/types";
import { HomeV2PlusWidget } from "scmp-app/components/home-v2/plus-widget";
import { useTopStoriesTracking } from "scmp-app/pages/home/hooks";
import {
  HeroArticle,
  HeroSecondaryArticles,
  StyledAdSlot,
  StyledContentItemHomeNativeArticle,
  TopStories,
} from "scmp-app/pages/home/styles";
import type { heroArticlesHomeV2Content$key } from "scmp-app/queries/__generated__/heroArticlesHomeV2Content.graphql";
import type { homeNativeArticleContentItemContent$key } from "scmp-app/queries/__generated__/homeNativeArticleContentItemContent.graphql";
import type { homePageQuery$data } from "scmp-app/queries/__generated__/homePageQuery.graphql";
import type { topStoriesHomeV2QueueItemsEdge$key } from "scmp-app/queries/__generated__/topStoriesHomeV2QueueItemsEdge.graphql";

type HeroTopStoriesProps = {
  adZone: string;
  heroEdge: TopStoriesEdge | undefined;
  heroRef?: Ref<HTMLDivElement>;
  heroSecondaryEdges: TopStoriesEdge[];
  // TODO [SCMPWEB-9518]: Remove after Proteus A/B test ends
  // When true (Proteus Suspense fallback), suppress top_story5+ impressions
  // so they don't fire on legacy content before Proteus resolves.
  isImpressionSuppressed?: boolean;
  isPlusDisabled: boolean;
  nativeAdsReference: homeNativeArticleContentItemContent$key;
  queryReference: homePageQuery$data;
  topStoriesEdges: topStoriesHomeV2QueueItemsEdge$key;
};

type TopStoriesEdge = NonNullable<
  NonNullable<NonNullable<homePageQuery$data["topStories"]>["items"]>["edges"][number]
>;

const HeroTopStories: FunctionComponent<HeroTopStoriesProps> = ({
  adZone,
  heroEdge,
  heroRef,
  heroSecondaryEdges,
  isImpressionSuppressed = true,
  isPlusDisabled,
  nativeAdsReference,
  queryReference,
  topStoriesEdges,
}) => {
  const listOfArticleIds = [
    heroEdge?.node?.entityId,
    ...heroSecondaryEdges.map(({ node }) => node?.entityId),
    ...(topStoriesEdges as unknown as ReadonlyArray<{ node: null | { entityId?: string } }>).map(
      ({ node }) => node?.entityId,
    ),
  ].filter(notEmpty);
  const {
    captureTrackImpressionEventElement2,
    captureTrackImpressionEventElement3,
    captureTrackImpressionEventElement4,
    captureTrackImpressionEventElement5,
  } = useTopStoriesTracking(listOfArticleIds);

  const getImpressionTrackingFunction = useCallback(
    (index: number) => {
      // TODO [SCMPWEB-9518]: Remove after Proteus A/B test ends
      // Suppress top_story5+ impressions when rendering legacy content as
      // Proteus Suspense fallback — only top_story1 (hero) should fire.
      if (isImpressionSuppressed) return;
      switch (index) {
        case 1:
          return captureTrackImpressionEventElement2;
        case 4:
          return captureTrackImpressionEventElement3;
        case 8:
          return captureTrackImpressionEventElement4;
        case 12:
          return captureTrackImpressionEventElement5;
      }
    },
    [
      captureTrackImpressionEventElement2,
      captureTrackImpressionEventElement3,
      captureTrackImpressionEventElement4,
      captureTrackImpressionEventElement5,
      isImpressionSuppressed,
    ],
  );

  return (
    <>
      {notEmpty(heroEdge?.node) && (
        <HeroArticle
          ref={heroRef}
          reference={heroEdge.node as unknown as heroArticlesHomeV2Content$key}
        />
      )}
      {notEmpty(heroSecondaryEdges) && <HeroSecondaryArticles reference={heroSecondaryEdges} />}
      {notEmpty(topStoriesEdges) && (
        <TopStories
          getImpressionTrackingFunction={getImpressionTrackingFunction}
          reference={topStoriesEdges}
          slots={{
            mobileInlineAds: (
              <StyledAdSlot
                adUnit="m_lrec1"
                breakpoint={theme.breakpoints.only("mobile")}
                sizes={[[300, 250]]}
                targeting={{
                  paid: TargetingPaidType.NotArticle,
                }}
                withBackground
                withLabel
                zone={adZone}
              />
            ),
            nativeAds: (
              <>
                <StyledAdSlot
                  adUnit="d_native2c"
                  breakpoint={theme.breakpoints.up("desktop")}
                  sizes={[[1, 1], "fluid"]}
                  targeting={{ paid: TargetingPaidType.NotArticle }}
                  zone={adZone}
                />
                <StyledAdSlot
                  adUnit="m_native2c"
                  breakpoint={theme.breakpoints.down("desktop")}
                  sizes={[[1, 1], "fluid"]}
                  targeting={{ paid: TargetingPaidType.NotArticle }}
                  zone={adZone}
                />
              </>
            ),
            plusWidget: !isPlusDisabled && <HomeV2PlusWidget reference={queryReference} />,
            sponsorArticle: (tracking, ref) =>
              notEmpty(nativeAdsReference) && (
                <StyledContentItemHomeNativeArticle
                  onClick={(entityId, urlAlias) => {
                    tracking({
                      entityId,
                      index: 3,
                      isSponsorArticle: true,
                      urlAlias,
                    });
                  }}
                  ref={ref}
                  reference={nativeAdsReference}
                />
              ),
          }}
        />
      )}
    </>
  );
};

HeroTopStories.displayName = "HeroTopStories";

export { HeroTopStories };
